import React, { useEffect, useMemo, useState } from "react";
import interiorCatalogApi from "../../services/interiorCatalogApi";

function emptyForm() {
  return {
    id: null,
    item_id: "",
    role: "mounting_screw",
    quantity: 1,
    position_json: { x: 0, y: 0, z: 0 },
    rotation_json: { x: 0, y: 0, z: 0 },
    parameters_json: {},
    processing_enabled: 1,
    bom_enabled: 1,
    sort_order: 0,
    status: "active",
  };
}

function num(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

export default function MountingItemsPanel({
  mounting,
  mountingItems = [],
  setMountingItems,
  availableItems = [],
  drillings = [],
  mountingScope = "asset_mounting",
}) {
  const [form, setForm] = useState(emptyForm());
  const [itemSearch, setItemSearch] = useState("");
const [itemSearchLoading, setItemSearchLoading] = useState(false);
const [itemSearchResults, setItemSearchResults] = useState([]);
const [componentTypeFilter, setComponentTypeFilter] = useState("");

  const selectedItem = useMemo(() => {
  const all = [...(availableItems || []), ...(itemSearchResults || [])];

  return all.find((x) => String(x.id) === String(form.item_id));
}, [availableItems, itemSearchResults, form.item_id]);

  const filteredAvailableItems = useMemo(() => {
  const source = itemSearch.trim()
    ? itemSearchResults
    : availableItems;

  return (source || []).filter((item) => {
    if (!componentTypeFilter) return true;

    const attrs = item.attributes_json || {};
    const itemKind = attrs.item_kind || item.item_kind || "";
    const componentType = attrs.component_type || item.component_type || "";

    return (
      String(itemKind).toLowerCase() === String(componentTypeFilter).toLowerCase() ||
      String(componentType).toLowerCase() === String(componentTypeFilter).toLowerCase()
    );
  });
}, [availableItems, itemSearchResults, itemSearch, componentTypeFilter]);

  function editItem(item) {
    setForm({
      id: item.id,
      item_id: item.item_id || "",
      role: item.role || "mounting_screw",
      quantity: item.quantity ?? 1,
      position_json: item.position_json || { x: 0, y: 0, z: 0 },
      rotation_json: item.rotation_json || { x: 0, y: 0, z: 0 },
      parameters_json: item.parameters_json || {},
      processing_enabled: item.processing_enabled !== 0 ? 1 : 0,
      bom_enabled: item.bom_enabled !== 0 ? 1 : 0,
      sort_order: item.sort_order || 0,
      status: item.status || "active",
    });
  }

  function newItem() {
    setForm(emptyForm());
  }

  function patchPosition(axis, value) {
  setForm((prev) => {
    const next = {
      ...prev,
      position_json: {
        ...(prev.position_json || {}),
        [axis]: num(value),
      },
    };

    syncPreviewFromForm(next);

    return next;
  });
}

function syncPreviewFromForm(nextForm) {
  if (!nextForm?.id) return;

  setMountingItems?.((prev) =>
    (prev || []).map((item) =>
      String(item.id) === String(nextForm.id)
        ? {
            ...item,
            role: nextForm.role,
            quantity: Number(nextForm.quantity || 1),
            position_json: nextForm.position_json || { x: 0, y: 0, z: 0 },
            rotation_json: nextForm.rotation_json || { x: 0, y: 0, z: 0 },
            parameters_json: nextForm.parameters_json || {},
            processing_enabled: nextForm.processing_enabled !== 0 ? 1 : 0,
            bom_enabled: nextForm.bom_enabled !== 0 ? 1 : 0,
            sort_order: Number(nextForm.sort_order || 0),
            status: nextForm.status || "active",
          }
        : item
    )
  );
}

  function patchRotation(axis, value) {
  setForm((prev) => {
    const next = {
      ...prev,
      rotation_json: {
        ...(prev.rotation_json || {}),
        [axis]: num(value),
      },
    };

    syncPreviewFromForm(next);

    return next;
  });
}

  function setFromDrilling(drill) {
    if (!drill) return;

    setForm((prev) => ({
      ...prev,
      position_json: {
        x: num(drill?.start?.x),
        y: num(drill?.start?.y),
        z: num(drill?.start?.z),
      },
      parameters_json: {
        ...(prev.parameters_json || {}),
        bindToDrillingId: drill.id,
        autoPlaceFromDrilling: true,
      },
    }));
  }

  async function save() {
  if (!mounting?.id) {
    alert("Save mounting first, then add hardware items.");
    return;
  }

  if (!form.item_id) {
    alert("Select item.");
    return;
  }

  const payload = {
    item_id: Number(form.item_id),
    role: form.role || "mounting_screw",
    quantity: Number(form.quantity || 1),
    position_json: form.position_json || { x: 0, y: 0, z: 0 },
    rotation_json: form.rotation_json || { x: 0, y: 0, z: 0 },
    parameters_json: form.parameters_json || {},
    processing_enabled: form.processing_enabled !== 0,
    bom_enabled: form.bom_enabled !== 0,
    sort_order: Number(form.sort_order || 0),
    status: form.status || "active",
  };

  try {
    if (form.id) {
      const res = await interiorCatalogApi.updateMountingItem(form.id, {
        ...payload,
        mounting_scope: mountingScope,
        mounting_id: mounting.id,
      });

      setMountingItems((prev) =>
        (prev || []).map((x) =>
          String(x.id) === String(form.id) ? res.item : x
        )
      );
    } else {
      const res =
        mountingScope === "item_mounting"
          ? await interiorCatalogApi.addItemMountingItem(mounting.id, payload)
          : await interiorCatalogApi.addAssetMountingItem(mounting.id, payload);

      setMountingItems((prev) => [...(prev || []), res.item]);

      setForm({
        ...emptyForm(),
        sort_order: (mountingItems || []).length + 1,
      });
    }
  } catch (err) {
    console.error("save mounting item error:", {
      message: err?.message,
      status: err?.response?.status,
      data: err?.response?.data,
    });

    alert(
      err?.response?.data?.message ||
        err?.message ||
        "Failed to save mounting item."
    );
  }
}

  async function remove() {
    if (!form.id) return;
    if (!window.confirm("Delete mounting item?")) return;

    await interiorCatalogApi.deleteMountingItem(form.id);

    setMountingItems((prev) =>
      (prev || []).filter((x) => String(x.id) !== String(form.id))
    );

    newItem();
  }

  useEffect(() => {
  const searchText = itemSearch.trim();

  if (!searchText) {
    setItemSearchResults([]);
    return;
  }

  const t = setTimeout(async () => {
    try {
      setItemSearchLoading(true);

      const res = await interiorCatalogApi.listFamilyItems({
        search: searchText,
        status: "active",
        limit: 100,
        offset: 0,
      });

      setItemSearchResults(Array.isArray(res) ? res : res?.items || []);
    } catch (err) {
      console.error("mounting item search error:", err);
      setItemSearchResults([]);
    } finally {
      setItemSearchLoading(false);
    }
  }, 300);

  return () => clearTimeout(t);
}, [itemSearch]);

  return (
    <div className="space-y-3">
      <div className="border rounded p-3 bg-white space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-sm">Mounting hardware</div>
            <div className="text-xs text-slate-500">
              Items physically attached to this asset mounting.
            </div>
          </div>

          <button
            type="button"
            onClick={newItem}
            className="px-3 py-1.5 border rounded bg-white text-sm"
          >
            + New
          </button>
        </div>

        {mountingItems.length ? (
          <div className="space-y-2">
            {mountingItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => editItem(item)}
                className={[
                  "w-full text-left border rounded p-2 text-sm",
                  String(form.id) === String(item.id)
                    ? "bg-blue-50 border-blue-300"
                    : "bg-white hover:bg-slate-50",
                ].join(" ")}
              >
                <div className="font-medium">
                  {item.item?.code || item.item?.name || `Item #${item.item_id}`}
                </div>
                <div className="text-xs text-slate-500">
                  {item.role || "—"} · qty {item.quantity}
                </div>
                <div className="text-[11px] text-slate-400">
                  X {item.position_json?.x ?? 0} · Y {item.position_json?.y ?? 0} · Z{" "}
                  {item.position_json?.z ?? 0}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-400">
            No hardware items attached to this mounting.
          </div>
        )}
      </div>

      <div className="border rounded p-3 bg-white space-y-3">
        <div className="font-semibold text-sm">
          {form.id ? "Edit mounting item" : "New mounting item"}
        </div>

        <div className="border rounded p-3 bg-slate-50 space-y-3">
  <div>
    <div className="font-semibold text-xs text-slate-600">
      Find hardware item
    </div>
    <div className="text-[11px] text-slate-400">
      Search by code, name or SKU. Example: screw, 4x16, SCREW.
    </div>
  </div>

  <Field label="Search item">
    <input
      value={itemSearch}
      onChange={(e) => setItemSearch(e.target.value)}
      placeholder="Search screw / dowel / handle..."
      className="w-full px-3 py-2 border rounded"
    />
  </Field>

  <Field label="Type filter">
    <select
      value={componentTypeFilter}
      onChange={(e) => setComponentTypeFilter(e.target.value)}
      className="w-full px-3 py-2 border rounded bg-white"
    >
      <option value="">All component types</option>
      <option value="hardware">hardware</option>
      <option value="screw">screw</option>
      <option value="dowel">dowel</option>
      <option value="connector">connector</option>
      <option value="handle">handle</option>
      <option value="hinge">hinge</option>
      <option value="runner">runner</option>
      <option value="bracket">bracket</option>
    </select>
  </Field>

  {itemSearchLoading ? (
    <div className="text-xs text-slate-500">Searching...</div>
  ) : null}

  <Field label={`Item (${filteredAvailableItems.length} results)`}>
    <select
      value={form.item_id}
      onChange={(e) => {
  const itemId = e.target.value;

  const all = [...(availableItems || []), ...(itemSearchResults || [])];
  const found = all.find((x) => String(x.id) === String(itemId));

  const attrs = found?.attributes_json || {};
  const componentType = attrs.component_type || found?.component_type || "";

  setForm((prev) => ({
    ...prev,
    item_id: itemId,
    role: componentType ? `mounting_${componentType}` : prev.role || "mounting_item",
  }));
}}
      className="w-full px-3 py-2 border rounded bg-white"
    >
      <option value="">Select item...</option>

      {filteredAvailableItems.map((item) => {
        const attrs = item.attributes_json || {};
        const kind = attrs.item_kind || item.item_kind || "";
        const type = attrs.component_type || item.component_type || "";

        return (
          <option key={item.id} value={item.id}>
            {item.code} · {item.name}
            {item.sku ? ` · ${item.sku}` : ""}
            {type ? ` · ${type}` : kind ? ` · ${kind}` : ""}
          </option>
        );
      })}
    </select>
  </Field>
</div>

        {selectedItem ? (
          <div className="rounded border bg-slate-50 p-2 text-xs text-slate-600">
            Selected: {selectedItem.code} · {selectedItem.name}
            {selectedItem.asset_id ? ` · asset ${selectedItem.asset_id}` : ""}
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-2">
          <Field label="Role">
            <input
              value={form.role || ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, role: e.target.value }))
              }
              className="w-full px-3 py-2 border rounded"
            />
          </Field>

          <Field label="Quantity">
            <input
              type="number"
              step="0.001"
              value={form.quantity}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  quantity: Number(e.target.value || 1),
                }))
              }
              className="w-full px-3 py-2 border rounded"
            />
          </Field>
        </div>

        <div className="border rounded p-3 bg-slate-50 space-y-2">
          <div className="font-semibold text-xs text-slate-600">Position</div>
          <div className="grid grid-cols-3 gap-2">
            {["x", "y", "z"].map((axis) => (
              <Field key={axis} label={axis.toUpperCase()}>
                <input
                  type="number"
                  value={form.position_json?.[axis] ?? 0}
                  onChange={(e) => patchPosition(axis, e.target.value)}
                  className="w-full px-2 py-1.5 border rounded"
                />
              </Field>
            ))}
          </div>
        </div>

        <div className="border rounded p-3 bg-slate-50 space-y-2">
          <div className="font-semibold text-xs text-slate-600">Rotation</div>
          <div className="grid grid-cols-3 gap-2">
            {["x", "y", "z"].map((axis) => (
              <Field key={axis} label={axis.toUpperCase()}>
                <input
                  type="number"
                  value={form.rotation_json?.[axis] ?? 0}
                  onChange={(e) => patchRotation(axis, e.target.value)}
                  className="w-full px-2 py-1.5 border rounded"
                />
              </Field>
            ))}
          </div>
        </div>

        {drillings.length ? (
          <Field label="Set position from drilling">
            <select
              value=""
              onChange={(e) => {
                const drill = drillings.find(
                  (x) => String(x.id) === String(e.target.value)
                );
                setFromDrilling(drill);
              }}
              className="w-full px-3 py-2 border rounded bg-white"
            >
              <option value="">Select drilling...</option>
              {drillings.map((drill) => (
                <option key={drill.id} value={drill.id}>
                  {drill.name || drill.id} · Ø{drill.diameter}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.processing_enabled !== 0}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                processing_enabled: e.target.checked ? 1 : 0,
              }))
            }
          />
          Processing enabled
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.bom_enabled !== 0}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                bom_enabled: e.target.checked ? 1 : 0,
              }))
            }
          />
          BOM enabled
        </label>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={save}
            className="px-3 py-2 bg-green-600 text-white rounded text-sm"
          >
            Save
          </button>

          <button
            type="button"
            onClick={remove}
            disabled={!form.id}
            className="px-3 py-2 border rounded text-red-600 text-sm disabled:opacity-40"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}